import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Image, StyleSheet, TouchableOpacity, View, ViewStyle } from 'react-native';

import { images } from '../assets/images';
import { colors, scale } from '../Styles';
import { DateConverters, SupportedDateFormat } from '../util/DateConverters';
import { MNOHSTextInput } from './MNOHSTextInput';

interface Props {
  style?: ViewStyle;
  /** The string format. */
  format: Extract<SupportedDateFormat, 'YYYY-MM-DD'>;
  /** The value to display. */
  value: string | Date;
  /** The maximum date to allow. */
  maximumDate?: Date;
  /** The minimum date to allow. */
  minimumDate?: Date;
  /** Called when the input's value changes. */
  onValueChange: (dateString: string, date: Date) => void;
}

/** A controlled date input component backed by React Datepicker for the web */
export const MNOHSDateStringInput = (props: Props) => {
  const converter = DateConverters[props.format];

  // see https://reactdatepicker.com/#example-custom-input
  const CustomInput = (args: { onClick?: () => void }) => {
    return (
      <View style={props.style}>
        <TouchableOpacity
          activeOpacity={0.75}
          onPress={event => {
            // need to do this to work on mobile
            event.preventDefault();
            args.onClick && args.onClick();
          }}
          style={styles.dropDownTouchable}>
          <MNOHSTextInput
            containerStyle={styles.textInputContainer}
            value={typeof props.value === 'string' ? props.value : converter.toString(props.value)}
            editable={false}
            pointerEvents={'none'}
          />
          <Image style={styles.dropDownArrowImage} source={images.caretUpIcon} resizeMode={'contain'} />
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <DatePicker
      popperPlacement="top"
      selected={typeof props.value === 'string' ? converter.toDate(props.value)! : props.value}
      minDate={new Date()}
      onChange={date => {
        if (date) {
          const castedDate = date as Date;
          const stringValue = converter.toString(castedDate);
          props.onValueChange(stringValue, castedDate);
        }
      }}
      customInput={<CustomInput />}
    />
  );
};

const styles = StyleSheet.create({
  dropDownTouchable: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.grayLight,
    borderRadius: scale(8),
    minHeight: scale(50),
    // iOS Shadowing
    shadowColor: colors.black,
    shadowRadius: 2,
    shadowOpacity: 0.5,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    // Android Shadowing
    elevation: 5,
  },
  iosDatePicker: {
    backgroundColor: colors.white,
    borderRadius: scale(10),
  },
  dropDownArrowImage: {
    aspectRatio: 1,
    width: '4%',
    marginRight: scale(10),
  },
  textInputContainer: {
    flex: 1,
  },
});
